import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import UserService from "../services/user.service";
import { Alert, Table, Button, Container, Modal, Form, ButtonGroup, Pagination, Spinner, Badge } from 'react-bootstrap';
import { FaPencilAlt, FaLock, FaLockOpen, FaTimes } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import { useSelector } from 'react-redux';
import { Autocomplete, TextField } from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';

const batchURI = process.env.REACT_APP_API_BATCH_URL;

// Wartezeit, bevor eine Sucheingabe an den Server geht. Ohne diese Verzoegerung
// loest jeder einzelne Tastendruck eine eigene Abfrage aus. Deren Antworten
// treffen nicht zwingend in der Sendereihenfolge ein, wodurch ein aelteres
// Ergebnis ein neueres ueberschreiben und die Liste ungefiltert wirken kann.
const SEARCH_DEBOUNCE_MS = 350;

// Datums-Formatierung Funktion
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Folgeprompt 16 (3.5): lokale ISO-Darstellung (YYYY-MM-DD) fuer <input type="date">.
// Bewusst aus lokalen Datumsteilen gebaut (nicht toISOString), um Zeitzonen-
// Verschiebungen um Mitternacht zu vermeiden.
const toLocalISODate = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '';
  const y = dateObj.getFullYear();
  const m = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const d = dateObj.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Heutiges Datum als YYYY-MM-DD (Vorbelegung "Gueltig ab").
const todayISODate = () => toLocalISODate(new Date());

// Folgeprompt 16 (3.5): "Gueltig bis" aus der Tarif-Laufzeit ableiten.
// fromStr = "Gueltig ab" (YYYY-MM-DD), value/unit aus t_tariffs
// (contract_term_value / contract_term_unit). Bei fehlender Laufzeit -> '' (der
// Aufrufer behaelt dann den bisherigen Wert; Feld bleibt manuell ausfuellbar).
const addTariffTerm = (fromStr, value, unit) => {
  if (!fromStr || value == null || value === '' || !unit) return '';
  const base = new Date(fromStr);
  if (isNaN(base)) return '';
  const n = parseInt(value, 10);
  if (isNaN(n)) return '';
  const d = new Date(base.getTime());
  switch (unit) {
    case 'day': d.setDate(d.getDate() + n); break;
    case 'week': d.setDate(d.getDate() + n * 7); break;
    case 'month': d.setMonth(d.getMonth() + n); break;
    case 'quarter': d.setMonth(d.getMonth() + n * 3); break;
    case 'year': d.setFullYear(d.getFullYear() + n); break;
    default: return '';
  }
  return toLocalISODate(d);
};

const SALES_CHANNEL_OPTIONS = [
  { id: 'b2b', label: 'B2B (Wiederverkäufer)' },
  { id: 'b2c', label: 'B2C (Endkunden / Startseite)' },
  { id: 'both', label: 'B2B & B2C (Beide)' },
];

const BatchManager = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [tariffQuery, setTariffQuery] = useState("");
  const [tariffMenuOpen, setTariffMenuOpen] = useState(false);

  const [isFormEnabled, setIsFormEnabled] = useState(false);
  
  const [batches, setBatches] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [services, setServices] = useState([]);
  // Produktarten (Hauptprodukte / "Oberprodukte") und aktuell gewaehltes Produkt.
  // Die Service-Auswahl im Charge-Modal wird auf das gewaehlte Produkt eingegrenzt.
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  // Filter der sichtbaren Liste nach Hauptprodukt (clientseitig auf der aktuellen Seite).
  const [productFilter, setProductFilter] = useState('');

  // Neue suchbare Filterzustände per Tastatureingabe
  const [batchNameFilter, setBatchNameFilter] = useState('');
  const [tariffFilter, setTariffFilter] = useState(null);
  const [salesChannelFilter, setSalesChannelFilter] = useState(null);
  const [allBatchNames, setAllBatchNames] = useState([]);

  const [subvos, setSubVos] = useState([]);
  // Folgeprompt 16 (Punkt 6): Statuswerte aus den Stammdaten (/statuses).
  const [statuses, setStatuses] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocTypeId, setSelectedDocTypeId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateInvoiceModal, setshowCreateInvoiceModal] = useState(false);
  // Bearbeiten-Modus: gleiche Modal-/Formularlogik wie beim Erstellen, aber mit
  // vorbefuellten Daten und Update-Aufruf. editBatchId haelt die Master-ID.
  const [isEditMode, setIsEditMode] = useState(false);
  const [editBatchId, setEditBatchId] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingText, setLoadingText] = useState(' Lade, bitte warten...');
  // searchInput ist der sofort sichtbare Feldinhalt, searchTerm der verzoegerte
  // Wert, mit dem tatsaechlich gesucht wird. Die Trennung haelt die Eingabe
  // fluessig und begrenzt die Serverabfragen auf eine je Suchbegriff.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('batch_name');
  const [sortOrder, setSortOrder] = useState('asc');
  // Laufende Nummer der juengsten Abfrage. Trifft die Antwort einer aelteren
  // Abfrage spaeter ein, wird sie verworfen statt die Liste zu ueberschreiben.
  const latestRequestRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, sortField, sortOrder, productFilter, tariffFilter, salesChannelFilter, batchNameFilter]);

  // Systemseitig definierte Hauptprodukte (Produktarten) einmalig laden.
  // Fehler werden bewusst still behandelt, damit die Charge-Erstellung auch
  // dann nutzbar bleibt, falls keine Produkte gepflegt sind.
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await UserService.getShowcaseBoard();
        setProducts(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        setProducts([]);
      }
    };
    loadProducts();
  }, []);

  // Folgeprompt 16 (Punkt 6): Statuswerte aus den Stammdaten laden. Fehler still
  // behandeln, damit die Charge-Erstellung auch ohne Status nutzbar bleibt.
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const res = await UserService.getStatusBoard();
        setStatuses(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        setStatuses([]);
      }
    };
    loadStatuses();
  }, []);

  // Nur die Services anzeigen, die dem gewaehlten Hauptprodukt zugeordnet sind
  // (t_services.showcase_id == ausgewaehltes Produkt). Ohne Produktauswahl leer.
  const filteredServices = selectedProductId
    ? services.filter((s) => String(s.showcase_id) === String(selectedProductId))
    : [];

  // Gewaehlter Service: WIRRT/qutel-Produkt wenn qutel_product_key gesetzt,
  // die Produktart "wirrt" heisst oder die Dienst-URL auf wirrt.com zeigt.
  const selectedService = services.find(
    (s) => String(s.service_id) === String(formData.service_id)
  );
  const selectedProductObj = products.find(
    (p) => String(p.showcase_id) === String(selectedProductId)
  );
  const isQutelService = !!(
    selectedService &&
    ((selectedService.qutel_product_key &&
      String(selectedService.qutel_product_key).trim()) ||
      String(selectedProductObj?.title || "").toLowerCase().includes("wirrt") ||
      String(selectedService.service_in_url || "").toLowerCase().includes("wirrt.com") ||
      String(selectedService.service_out_url || "").toLowerCase().includes("wirrt.com"))
  );

  // Tarif-Filter:
  // 1) Bei WIRRT/qutel-Services sind ausschliesslich Tarife mit aktivem
  //    qutel_sync und vergebener qutel_sequence zulaessig.
  // 2) Ist am Tarif ein Service (oder Produkt) hinterlegt, muss dieser zum
  //    gewaehlten Service/Produkt passen.
  // 3) Unzugeordnete Bestandstarife (service_id & showcase_id leer) werden bei
  //    qutel-Services angezeigt, wenn qutel_sync aktiv ist, sonst bei Nicht-qutel.
  const filteredTariffs = tariffs.filter((t) => {
    const sync = t.qutel_sync === true || t.qutel_sync === 1;
    const hasSeq = Boolean(t.qutel_sequence && String(t.qutel_sequence).trim());
    const hasTariffService = t.service_id != null && String(t.service_id) !== "";
    const hasTariffProduct = t.showcase_id != null && String(t.showcase_id) !== "";

    if (isQutelService && (!sync || !hasSeq)) {
      return false;
    }

    if (hasTariffService) {
      if (String(t.service_id) !== String(formData.service_id)) return false;
      return true;
    }
    if (hasTariffProduct) {
      if (String(t.showcase_id) !== String(selectedProductId)) return false;
      return true;
    }
    return isQutelService ? sync : !sync;
  });

  // Dienst-Eignung (Produktdefinition "Geeignet für") begrenzt die Zugangsart:
  // HUMAN → nur Menschen, AI → nur KI, BOTH/keine Angabe → alle Optionen.
  const PARTICIPANT_TYPE_LABELS = { HUMAN: 'Nur Menschen', AI: 'Nur KI', BOTH: 'Menschen und KI' };
  const serviceSuitability = selectedService ? (selectedService.participant_type || null) : null;
  const allowedParticipantTypes = !formData.service_id
    ? []
    : serviceSuitability === 'HUMAN'
      ? ['HUMAN']
      : serviceSuitability === 'AI'
        ? ['AI']
        : ['HUMAN', 'AI', 'BOTH'];
  const participantTypeLocked = isEditMode && selectedBatch?.participant_type_locked === true;

  // Service-Wechsel: unzulässige Zugangsart korrigieren, eindeutige vorbelegen.
  useEffect(() => {
    if (participantTypeLocked) return;
    setFormData((prev) => {
      const svc = services.find((s) => String(s.service_id) === String(prev.service_id));
      const suit = svc ? (svc.participant_type || null) : null;
      const allowed = !prev.service_id
        ? []
        : suit === 'HUMAN' ? ['HUMAN'] : suit === 'AI' ? ['AI'] : ['HUMAN', 'AI', 'BOTH'];
      const current = prev.participant_type || '';
      if (!prev.service_id) {
        return current ? { ...prev, participant_type: '' } : prev;
      }
      if (current && !allowed.includes(current)) {
        return { ...prev, participant_type: allowed.length === 1 ? allowed[0] : '' };
      }
      if (!current && allowed.length === 1) {
        return { ...prev, participant_type: allowed[0] };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.service_id, services, participantTypeLocked]);

  const selectedTariff = tariffs.find(
    (t) => String(t.tariff_id) === String(formData.tariff_id)
  );
  const usedCodesForTariff = selectedTariff
    ? batches
        .filter((b) => String(b.tariff_id) === String(selectedTariff.tariff_id))
        .reduce((sum, b) => sum + (Number(b.batch_quantity) || 0), 0)
    : 0;
  const qutelRemaining =
    selectedTariff && (selectedTariff.qutel_sync === true || selectedTariff.qutel_sync === 1)
      ? Math.max(0, (Number(selectedTariff.qutel_max_codes) || 0) - usedCodesForTariff)
      : null;

  // Ermittelt das Hauptprodukt (Showcase) einer Charge ueber ihren Service.
  const productForBatch = (batch) => {
    const svc = services.find((s) => String(s.service_id) === String(batch.service_id));
    if (!svc || svc.showcase_id == null) return null;
    return products.find((p) => String(p.showcase_id) === String(svc.showcase_id)) || null;
  };
  const productNameForBatch = (batch) => {
    const prod = productForBatch(batch);
    return prod ? prod.title : '-';
  };

  // Sichtbare Charge-Liste: Alle Filter (Hauptprodukt, Chargenname, Tarif, Vertriebsart)
  // werden serverseitig in der Paginierung angewendet.
  const displayedBatches = batches;

const loadBatches = async () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      const response = await UserService.getBatchBoardMaster({
        page: currentPage,
        search: batchNameFilter || searchTerm,
        sort: sortField,
        order: sortOrder,
        showcase_id: productFilter || null,
        tariff_id: tariffFilter ? tariffFilter.tariff_id : null,
        sales_channel: salesChannelFilter ? salesChannelFilter.id : null,
        batch_name: batchNameFilter || null,
      });
      const responseservices = await UserService.getServiceBoard();
      const responsetariff = await UserService.getTariffBoard();
      const responsesubvos = await UserService.getSubVosBoard();
      // Admins pflegen systemweite Belegarten. Ohne den expliziten Scope kann
      // der Owner-Resolver ROLE_ADMIN nicht zuordnen und antwortet mit 403;
      // dadurch blieben auch die zuvor geladenen Services und Tarife unsichtbar.
      const responseDocTypes = await UserService.getDocumentTypes(
        currentUser?.roles?.includes('ROLE_ADMIN') ? 'system' : undefined
      );
      
      const docTypes = responseDocTypes.data; // Daten zwischenspeichern

      // Zwischenzeitlich wurde bereits eine neuere Abfrage gestartet. Deren
      // Ergebnis ist massgeblich, dieses hier waere veraltet.
      if (requestId !== latestRequestRef.current) return;

      setTariffs(responsetariff.data.tariffs);
      setServices(responseservices.data); 
      setBatches(response.data.batches);
      setSubVos(responsesubvos.data);
      setDocumentTypes(docTypes); // Alle Belegarten im State speichern
      setTotalPages(response.data.totalPages);

      if (response.data.allBatchNames && Array.isArray(response.data.allBatchNames)) {
        setAllBatchNames(response.data.allBatchNames);
      }

      // +++ ANFANG: NEUE LOGIK ZUR VORAUSWAHL DES STANDARDS +++
      const defaultDocType = docTypes.find(doc => doc.is_default);
      if (defaultDocType) {
        setSelectedDocTypeId(defaultDocType.doc_type_id);
      } else {
        setSelectedDocTypeId(''); // Zurücksetzen, falls kein Standard gefunden wird
      }
      // +++ ENDE: NEUE LOGIK +++
      
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      setApiErrors('Fehler beim Laden der Batches: ' + error);
    } finally {
      // Die Ladeanzeige darf nur die juengste Abfrage beenden, sonst
      // verschwindet sie, waehrend noch auf das aktuelle Ergebnis gewartet wird.
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    setIsFormEnabled(isFormValid()); // Aktualisiert den Button-Status
  }, [formData, errors]); // Wird bei jeder Änderung von formData oder errors ausgeführt

  useEffect(() => {
    setIsFormEnabled(isFormValid3()); // Aktualisiert den Button-Status
  }, [formData, errors]); // Wird bei jeder Änderung von formData oder errors ausgeführt

  const handleSearchChange = (e) => {
    // Nur den Feldinhalt setzen. Die eigentliche Suche startet verzoegert
    // ueber den zugehoerigen Effekt, der auch die Seite zuruecksetzt.
    setSearchInput(e.target.value);
  };

  // Leert das Suchfeld und zeigt sofort wieder die vollstaendige Liste.
  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    const newSortOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newSortOrder);
  };

  const handleCreateClick = () => {
    // Folgeprompt 16 (3.5): "Gueltig ab" mit dem heutigen Datum vorbelegen.
    setIsEditMode(false);
    setEditBatchId(null);
    setSelectedBatch(null);
    setFormData({
      batch_name: '',
      batch_quantity: 0,
      sub_vo_id: 0,
      user_id: 0,
      service_id: 0,
      tariff_id: 0,
      validity_from_date: todayISODate(),
      validity_until_date: '',
      is_processed: false,
      status_id: '',
      participant_type: '',
      is_b2b: true,
      is_b2c: false,
      is_homepage_demo: false,
    });
    setSelectedProductId('');
    setErrors({});
    setShowCreateModal(true);
  };

  // Charge nachtraeglich bearbeiten. Nur zulaessig, solange nichts fakturiert
  // (is_processed) und nicht gesperrt (is_locked) ist – die Icons sind sonst
  // deaktiviert. Formular wird aus dem Datensatz vorbefuellt.
  const handleEditClick = (batch) => {
    const prod = productForBatch(batch);
    setIsEditMode(true);
    setEditBatchId(batch.batch_id);
    setSelectedBatch(batch);
    setFormData({
      batch_name: batch.batch_name || '',
      batch_quantity: batch.batch_quantity || 0,
      sub_vo_id: batch.sub_vo_id || 0,
      user_id: batch.user_id || 0,
      service_id: batch.service_id || '',
      tariff_id: batch.tariff_id || '',
      validity_from_date: batch.validity_from_date ? toLocalISODate(new Date(batch.validity_from_date)) : '',
      validity_until_date: batch.validity_until_date ? toLocalISODate(new Date(batch.validity_until_date)) : '',
      is_processed: !!batch.is_processed,
      status_id: batch.status_id || '',
      participant_type: batch.participant_type || '',
      // Bestandschargen ohne gesetzte Vertriebsart gelten weiterhin als B2B.
      is_b2b: batch.is_b2b === undefined ? true : !!batch.is_b2b,
      is_b2c: !!batch.is_b2c,
      is_homepage_demo: !!batch.is_homepage_demo,
    });
    setSelectedProductId(prod ? String(prod.showcase_id) : '');
    setErrors({});
    setApiErrors('');
    setShowCreateModal(true);
  };

  // Charge sperren/entsperren. Bei Sperrung werden auch bereits im Umlauf
  // befindliche Codes serverseitig abgewiesen (Weiterleitung auf Sperr-Seite).
  const handleToggleLock = async (batch) => {
    const willLock = !batch.is_locked;
    const confirmMsg = willLock
      ? `Charge "${batch.batch_name}" wirklich sperren? Bereits ausgegebene Codes funktionieren dann nicht mehr.`
      : `Charge "${batch.batch_name}" wieder entsperren?`;
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    setApiErrors('');
    try {
      await UserService.toggleBatchLock(batch.batch_id, willLock);
      await loadBatches();
    } catch (error) {
      const serverMsg = error?.response?.data?.message;
      setApiErrors(serverMsg || 'Fehler beim Sperren/Entsperren der Charge.');
    } finally {
      setLoading(false);
    }
  };

  // Folgeprompt 16 (3.5): Tarifwechsel -> "Gueltig bis" aus der Laufzeit neu
  // berechnen (relativ zu "Gueltig ab"). Ohne hinterlegte Laufzeit bleibt der
  // bisherige Wert erhalten (Feld manuell ausfuellbar).
  const handleTariffChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const tariff = tariffs.find((t) => String(t.tariff_id) === String(value));
      const until = tariff
        ? addTariffTerm(prev.validity_from_date, tariff.contract_term_value, tariff.contract_term_unit)
        : '';
      return {
        ...prev,
        tariff_id: value,
        validity_until_date: until || prev.validity_until_date,
      };
    });
    validateField('tariff_id', value);
  };

  // Folgeprompt 16 (3.5): Aenderung von "Gueltig ab" -> "Gueltig bis" anhand des
  // aktuell gewaehlten Tarifs neu berechnen (sofern eine Laufzeit hinterlegt ist).
  const handleFromDateChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const tariff = tariffs.find((t) => String(t.tariff_id) === String(prev.tariff_id));
      const until = tariff
        ? addTariffTerm(value, tariff.contract_term_value, tariff.contract_term_unit)
        : '';
      return {
        ...prev,
        validity_from_date: value,
        validity_until_date: until || prev.validity_until_date,
      };
    });
    validateField('validity_from_date', value);
  };

  // Produktwechsel: gewaehltes Produkt setzen und die Service-Auswahl
  // zuruecksetzen, da nur Services des neuen Produkts gueltig sind.
  const handleProductChange = (e) => {
    const value = e.target.value;
    setSelectedProductId(value);
    setFormData((prev) => ({ ...prev, service_id: '' }));
    validateField('service_id', '');
  };

  const handleCreateInvoiceClick = (batch) => {
    setFormData(batch);
    console.log("batch:"+batch);
    setErrors({});
    setshowCreateInvoiceModal(true);
  };

const handleCreateInvoiceSubmit = async (e) => {
     e.preventDefault();
      setLoading(true);
  
      if (!isFormValid3() || !selectedDocTypeId) { // Prüfung erweitert
        setLoading(false);
        setApiErrors('Bitte wählen Sie eine Belegart und einen Wiederverkäufer aus!');
        return;
      }
   
      try {
        // Die ausgewählte ID wird dem Datenobjekt hinzugefügt
        const payload = { ...formData, doc_type_id: selectedDocTypeId };
        await UserService.createInvoice(payload); // payload wird gesendet
        
        setshowCreateInvoiceModal(false);
        loadBatches();
        setApiErrors(null);
      } catch (error) {
        // Konkrete Servermeldung anzeigen (analog zum Charge-Anlegen-Handler), sonst Fallback.
        const serverMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.details;
        setApiErrors(serverMsg || 'Fehler beim Erstellen des Batches');
      } finally {
        setLoading(false);
      }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Vertriebsart wird ueber Checkboxen gepflegt und liefert boolesche Werte.
    if (type === 'checkbox') {
      setFormData((prevFormData) => {
        const next = { ...prevFormData, [name]: checked };
        if (name === 'is_b2c' && !checked) next.is_homepage_demo = false;
        if (name === 'is_homepage_demo' && checked) next.is_b2c = true;
        return next;
      });
      return;
    }

    setFormData((prevFormData) => {
      const next = {
        ...prevFormData,
        [name]: name === 'batch_quantity' ? parseFloat(value) || '' : value,
      };
      // Servicewechsel: Tarif zuruecksetzen (Filter qutel/nicht-qutel aendert sich).
      if (name === 'service_id') {
        next.tariff_id = '';
      }
      return next;
    });

    validateField(name, value);
    if (name === 'service_id') {
      validateField('tariff_id', '');
    }
  };
  
  const handleBlur = (e) => {
    const { name, value } = e.target;
  
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: name === 'batch_quantity' ? parseFloat(value) || '' : value, // Stellt sicher, dass batch_quantity eine Zahl ist
    }));
  
    validateField(name, value); // Validiert das Feld bei jeder Änderung
  };

  const validateField = (fieldName, value) => {
    let fieldErrors = {};
  
    switch (fieldName) {
      case 'batch_name':
        if (!value) fieldErrors.batch_name = 'Batch-Name ist ein Pflichtfeld';
        break;
        
      case 'batch_quantity':
        if (!value) {
          fieldErrors.batch_quantity = 'Anzahl ist ein Pflichtfeld';
        } else if (isNaN(value)) {
          fieldErrors.batch_quantity = 'Anzahl muss eine Zahl sein';
        } else if (value <= 0) {
          fieldErrors.batch_quantity = 'Anzahl muss eine positive Zahl sein';
        }
        break;

      case 'participant_type':
        if (!['HUMAN', 'AI', 'BOTH'].includes(value)) {
          fieldErrors.participant_type = 'Bitte festlegen, ob die Charge für Menschen, KI oder beide gilt.';
        }
        break;
        
      case 'tariff_id':
        const numericValue1 = parseInt(value, 10);
        if (!value || isNaN(numericValue1)) fieldErrors.tariff_id = 'Tarif-ID muss eine Zahl sein';
        break;
        
      case 'sub_vo_id':
        const numericValue2 = parseInt(value, 10);
        if (!value || isNaN(numericValue2)) fieldErrors.sub_vo_id = 'Wiederverkäufer-ID muss eine Zahl sein';
        break;
        
      case 'service_id':
        const numericValue3 = parseInt(value, 10);
        if (!value || isNaN(numericValue3)) fieldErrors.service_id = 'Service-ID muss eine Zahl sein';
        break;
        
      case 'validity_from_date':
        if (!value) fieldErrors.validity_from_date = 'Gültig ab-Datum ist ein Pflichtfeld';
        break;
        
      case 'validity_until_date':
        if (!value) fieldErrors.validity_until_date = 'Gültig bis-Datum ist ein Pflichtfeld';
        break;
        
      default:
        break;
    }
  
    console.log("Formulardaten:", fieldName, value);
    console.log("Fehler:", JSON.stringify(fieldErrors));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: fieldErrors[fieldName],
    }));
  };
  
  const isParticipantTypeValid = () => (
    ['HUMAN', 'AI', 'BOTH'].includes(formData.participant_type) ||
    (isEditMode && !selectedBatch?.participant_type && !formData.participant_type)
  );

  // Mindestens eine Vertriebsart muss gewaehlt sein, beide sind erlaubt.
  const isSalesChannelValid = () => !!formData.is_b2b || !!formData.is_b2c;

  // B2C ist nur moeglich, wenn der gewaehlte Dienst mindestens eine nicht
  // physikalische Transportart besitzt - gleiche Regel wie im Backend.
  const serviceHasDigitalTransport = !!selectedService
    && Array.isArray(selectedService.transportTypes)
    && selectedService.transportTypes.some((tt) => !tt.is_physical && tt.is_active);

  const isFormValid = () => {
    return (
      Object.keys(errors).every((key) => !errors[key]) && // Keine Fehler in den Feldern
      formData.batch_name && formData.batch_name.trim() !== '' && // Kein leerer String
      formData.batch_quantity > 0 && // Muss größer als 0 sein
      formData.tariff_id !== '' && // Kein leerer String
      formData.sub_vo_id !== '' && // Kein leerer String
      formData.service_id !== '' && // Kein leerer String
      formData.validity_from_date !== '' && // Kein leerer String
      formData.validity_until_date !== '' && // Kein leerer String
      isParticipantTypeValid() &&
      isSalesChannelValid()
    );
  };

  const isFormValid3 = () => {
    return (
      Object.keys(errors).every((key) => !errors[key]) && // Keine Fehler in den Feldern
      formData.sub_vo_id !== '' 
    );
  };
  
  const isFormValid2 = () => {
    if (!formData) return false; // Stellt sicher, dass formData definiert ist
    return (
      Object.keys(errors).every((key) => !errors[key]) && 
      formData.batch_name && formData.batch_name.trim() !== '' &&
      formData.batch_quantity > 0 &&
      formData.tariff_id && formData.tariff_id !== '' &&
      formData.sub_vo_id && formData.sub_vo_id !== '' &&
      formData.service_id && formData.service_id !== '' &&
      formData.validity_from_date && formData.validity_from_date !== '' &&
      formData.validity_until_date && formData.validity_until_date !== '' &&
      isParticipantTypeValid() &&
      isSalesChannelValid()
    );
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isFormValid()) {
      setLoading(false);
      setApiErrors('Bitte korrigieren Sie die Fehler im Formular.');
      return;
    }

    try {
      // status_id additiv: leere Auswahl -> null, sonst Integer.
      const payload = {
        ...formData,
        status_id: formData.status_id ? parseInt(formData.status_id, 10) : null,
      };
      // Bestehende NULL-Legacy-Chargen dürfen in anderen Feldern weiter
      // gepflegt werden. Nur eine explizite Auswahl klassifiziert sie.
      if (isEditMode && !selectedBatch?.participant_type && !formData.participant_type) {
        delete payload.participant_type;
      }
      if (isEditMode && editBatchId) {
        await UserService.updateBatchBoardMaster(editBatchId, payload);
      } else {
        await UserService.createBatchBoardMaster(payload);
      }
      setShowCreateModal(false);
      setIsEditMode(false);
      setEditBatchId(null);
      loadBatches();
    } catch (error) {
      // Konkrete Servermeldung anzeigen (z. B. doppelter Name), sonst Fallback.
      const serverMsg = error?.response?.data?.message;
      setApiErrors(serverMsg || (isEditMode ? 'Fehler beim Aktualisieren der Charge' : 'Fehler beim Erstellen des Batches'));
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilter = Boolean(
    batchNameFilter ||
    salesChannelFilter ||
    tariffFilter ||
    productFilter ||
    searchTerm
  );

  const handleResetAllFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setBatchNameFilter('');
    setSalesChannelFilter(null);
    setTariffFilter(null);
    setProductFilter('');
    setCurrentPage(1);
  };

  return (
    <Container style={{ paddingTop: '20px' }}>
      <style jsx="true">{`
        .apple-blue-btn {
          background-color: #007AFF;
          border-color: #007AFF;
          border-radius: 8px;
          padding: 10px 20px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .apple-blue-btn:hover {
          background-color: #0056b3;
          border-color: #0056b3;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
        }
        .modern-table {
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
          margin-top: 20px;
        }
        .modern-table thead {
          background-color: #007AFF;
          color: white;
        }
        .modern-table th {
          cursor: pointer;
          font-weight: 600;
          padding: 15px 10px;
          transition: background-color 0.2s ease;
        }
        .modern-table th:hover {
          background-color: #0056b3;
        }
        .modern-table td {
          padding: 12px 10px;
          vertical-align: middle;
        }
        .batch-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          margin-bottom: 15px;
          padding: 15px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .batch-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }
        .batch-card-header {
          color: #007AFF;
          font-weight: 600;
          margin-bottom: 10px;
          font-size: 18px;
        }
        .batch-card-item {
          display: flex;
          justify-content: space-between;
          padding: 5px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .batch-card-item:last-child {
          border-bottom: none;
        }
        .batch-card-label {
          color: #666;
          font-size: 14px;
        }
        .batch-card-value {
          color: #333;
          font-weight: 500;
        }
        .mobile-view {
          display: none;
        }
        .desktop-view {
          display: block;
        }
        @media (max-width: 768px) {
          .mobile-view {
            display: block;
          }
          .desktop-view {
            display: none;
          }
          .container {
            padding: 10px;
          }
        }
        .search-box {
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          padding: 8px 12px;
          transition: border-color 0.2s ease;
        }
        .search-box:focus {
          border-color: #007AFF;
          box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
        }
        /* Suchfeld mit Loeschsymbol. Das Symbol liegt innerhalb des Feldes,
           daher braucht die Eingabe rechts zusaetzlichen Innenabstand. */
        .myn-search-field {
          position: relative;
          flex: 1 1 220px;
          max-width: 300px;
          min-width: 0;
        }
        .myn-search-field .search-box {
          width: 100%;
          padding-right: 40px;
        }
        .myn-search-clear {
          position: absolute;
          top: 50%;
          right: 4px;
          transform: translateY(-50%);
          /* Mindestens 44 x 44 Pixel Beruehrflaeche fuer die Bedienung am
             Touchscreen, waehrend das Symbol selbst dezent klein bleibt. */
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          color: #b0b0b0;
          font-size: 13px;
          line-height: 1;
          cursor: pointer;
          border-radius: 8px;
          transition: color 0.2s ease, background-color 0.2s ease;
        }
        .myn-search-clear:hover {
          color: #666666;
          background-color: rgba(0, 0, 0, 0.05);
        }
        .myn-search-clear:focus-visible {
          outline: 2px solid #007AFF;
          outline-offset: 2px;
          color: #666666;
        }
        @media (prefers-reduced-motion: reduce) {
          .myn-search-clear { transition: none; }
        }
        @media (prefers-color-scheme: dark) {
          .myn-search-clear { color: #8a8a8e; }
          .myn-search-clear:hover {
            color: #d1d1d6;
            background-color: rgba(255, 255, 255, 0.08);
          }
        }
        .chargen-panel {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
          padding: 28px;
          margin-top: 10px;
        }
        @media (max-width: 768px) {
          .chargen-panel {
            padding: 16px;
            border-radius: 12px;
          }
        }
      `}</style>

      <div className="chargen-panel">
      <h1 style={{ color: '#333', marginBottom: '30px' }}>Chargenmanager</h1>
      {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
      
      {/* Such- & Filterleiste mit durchsuchbaren Dropdowns per Zeicheneingabe */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button className="apple-blue-btn" onClick={handleCreateClick} style={{ whiteSpace: 'nowrap', minHeight: '40px', height: '40px' }}>
          Charge erstellen
        </Button>

        {/* 1. Filter: Chargenname (suchbar per Texteingabe mit Vorschlägen) */}
        <Autocomplete
          size="small"
          freeSolo
          options={allBatchNames}
          value={batchNameFilter || ''}
          onChange={(event, newValue) => {
            setBatchNameFilter(newValue || '');
            setCurrentPage(1);
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setBatchNameFilter(newInputValue || '');
              setCurrentPage(1);
            }
          }}
          sx={{
            minWidth: 220,
            flex: '1 1 200px',
            maxWidth: 320,
            background: '#fff',
            borderRadius: '8px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '0.875rem',
              backgroundColor: '#fff',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Chargenname suchen"
              placeholder="Tippen zum Suchen …"
              aria-label="Chargenname suchen"
            />
          )}
        />

        {/* 2. Filter: Vertriebsart (suchbar per Texteingabe) */}
        <Autocomplete
          size="small"
          options={SALES_CHANNEL_OPTIONS}
          getOptionLabel={(option) => (option ? option.label : '')}
          value={salesChannelFilter}
          onChange={(event, newValue) => {
            setSalesChannelFilter(newValue);
            setCurrentPage(1);
          }}
          isOptionEqualToValue={(option, val) => option?.id === val?.id}
          sx={{
            minWidth: 210,
            flex: '1 1 200px',
            maxWidth: 290,
            background: '#fff',
            borderRadius: '8px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '0.875rem',
              backgroundColor: '#fff',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vertriebsart"
              placeholder="z. B. B2B, B2C …"
              aria-label="Vertriebsart auswählen"
            />
          )}
        />

        {/* 3. Filter: Tarifname (suchbar per Texteingabe) */}
        <Autocomplete
          size="small"
          options={tariffs}
          getOptionLabel={(option) => (option ? `${option.tariff_name || ''}` : '')}
          value={tariffFilter}
          onChange={(event, newValue) => {
            setTariffFilter(newValue);
            setCurrentPage(1);
          }}
          isOptionEqualToValue={(option, val) => String(option?.tariff_id) === String(val?.tariff_id)}
          sx={{
            minWidth: 200,
            flex: '1 1 200px',
            maxWidth: 290,
            background: '#fff',
            borderRadius: '8px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '0.875rem',
              backgroundColor: '#fff',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tarifname"
              placeholder="Tippen zum Suchen …"
              aria-label="Tarifname auswählen"
            />
          )}
        />

        {/* 4. Filter: Hauptprodukt (suchbar per Texteingabe) */}
        <Autocomplete
          size="small"
          options={products}
          getOptionLabel={(option) => (option ? option.title || '' : '')}
          value={products.find((p) => String(p.showcase_id) === String(productFilter)) || null}
          onChange={(event, newValue) => {
            setProductFilter(newValue ? String(newValue.showcase_id) : '');
            setCurrentPage(1);
          }}
          isOptionEqualToValue={(option, val) => String(option?.showcase_id) === String(val?.showcase_id)}
          sx={{
            minWidth: 190,
            flex: '1 1 190px',
            maxWidth: 270,
            background: '#fff',
            borderRadius: '8px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '0.875rem',
              backgroundColor: '#fff',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Hauptprodukt"
              placeholder="z. B. Google Review …"
              aria-label="Hauptprodukt auswählen"
            />
          )}
        />

        {/* Reset-Button wenn mindestens ein Filter aktiv ist */}
        {hasActiveFilter && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleResetAllFilters}
            style={{ minHeight: 40, height: 40, display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            title="Alle gesetzten Filter zurücksetzen"
          >
            <FaTimes /> Filter zurücksetzen
          </Button>
        )}
      </div>
      
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner animation="border" style={{ color: '#007AFF' }} />
          <p style={{ marginTop: '10px', color: '#666' }}>Lade, bitte warten...</p>
        </div>
      )}
      
      {/* Desktop View */}
      <div className="desktop-view">
        <Table className="modern-table" hover responsive>
          <thead>
            <tr>
              <th onClick={() => handleSortChange('batch_id')}>Chargen-ID</th>
              <th onClick={() => handleSortChange('batch_name')}>Chargenname</th>
              <th>Vertriebsart</th>
              <th>Mietmenge</th>
              <th onClick={() => handleSortChange('batch_quantity')}>Chargenmenge</th>
              <th onClick={() => handleSortChange('sub_vo_id')}>Wiederverkäufer-ID</th>
              <th onClick={() => handleSortChange('t_sub_vo.company_name')}>Wiederverkäufer Firmenname</th>
              <th>Hauptprodukt</th>
              <th onClick={() => handleSortChange('service_id')}>Service-ID</th>
              <th onClick={() => handleSortChange('t_services.service_name')}>Service-Name</th>
              <th onClick={() => handleSortChange('tariff_id')}>Tarif-ID</th>
              <th onClick={() => handleSortChange('t_tariffs.tariff_name')}>Tarif-Name</th>
              <th onClick={() => handleSortChange('validity_from_date')}>Gültig ab</th>
              <th onClick={() => handleSortChange('validity_until_date')}>Gültig bis</th>
              <th>Verarbeitet (Status)</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {displayedBatches.map(batch => (
              <tr key={batch.batch_id}>
                <td>{batch.batch_id}</td>
                <td><strong>{batch.batch_name}</strong></td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {batch.is_b2b ? <Badge bg="primary" style={{ fontSize: '0.75rem' }}>B2B</Badge> : null}
                    {batch.is_b2c ? <Badge bg="success" style={{ fontSize: '0.75rem' }}>B2C</Badge> : null}
                    {!batch.is_b2b && !batch.is_b2c ? <span className="text-muted">-</span> : null}
                  </div>
                </td>
                <td>-</td>
                <td>{batch.batch_quantity}</td>
                <td>{batch.sub_vo_id ? batch.sub_vo_id : '-'}</td>
                <td>{batch.sub_vo_id && batch.t_sub_vo ? batch.t_sub_vo.company_name : '-'}</td>
                <td>{productNameForBatch(batch)}</td>
                <td>{batch.service_id}</td>
                <td>{batch.t_services ? batch.t_services.service_name : 'N/A'}</td>
                <td>{batch.tariff_id}</td>
                <td>{batch.t_tariffs ? batch.t_tariffs.tariff_name : 'N/A'}</td>
                <td>{formatDate(batch.validity_from_date)}</td>
                <td>{formatDate(batch.validity_until_date)}</td>
                <td>
                  {batch.is_locked
                    ? <span style={{ color: '#dc3545', fontWeight: 600 }}>Gesperrt</span>
                    : (batch.status ? batch.status.status_name : '-')}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <Button 
                      className="apple-blue-btn" 
                      size="sm"
                      disabled={batch.is_processed || batch.is_locked} 
                      onClick={() => handleCreateInvoiceClick(batch)}
                    >
                      Bestellen & Fakturieren
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      title={batch.is_processed ? 'Bereits fakturiert – keine Bearbeitung möglich' : (batch.is_locked ? 'Gesperrt – bitte zuerst entsperren' : 'Charge bearbeiten')}
                      aria-label="Charge bearbeiten"
                      disabled={batch.is_processed || batch.is_locked}
                      onClick={() => handleEditClick(batch)}
                    >
                      <FaPencilAlt />
                    </Button>
                    <Button
                      variant={batch.is_locked ? 'danger' : 'outline-danger'}
                      size="sm"
                      title={batch.is_locked ? 'Charge entsperren' : 'Charge sperren'}
                      aria-label={batch.is_locked ? 'Charge entsperren' : 'Charge sperren'}
                      onClick={() => handleToggleLock(batch)}
                    >
                      {batch.is_locked ? <FaLockOpen /> : <FaLock />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      
      {/* Mobile View */}
      <div className="mobile-view">
        {displayedBatches.map(batch => (
          <div key={batch.batch_id} className="batch-card">
            <div className="batch-card-header">
              {batch.batch_name} (ID: {batch.batch_id})
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Vertriebsart:</span>
              <span className="batch-card-value">
                {batch.is_b2b ? <Badge bg="primary" className="me-1" style={{ fontSize: '0.75rem' }}>B2B</Badge> : null}
                {batch.is_b2c ? <Badge bg="success" style={{ fontSize: '0.75rem' }}>B2C</Badge> : null}
                {!batch.is_b2b && !batch.is_b2c ? '-' : null}
              </span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Hauptprodukt:</span>
              <span className="batch-card-value">{productNameForBatch(batch)}</span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Mietmenge:</span>
              <span className="batch-card-value">-</span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Chargenmenge:</span>
              <span className="batch-card-value">{batch.batch_quantity}</span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Wiederverkäufer:</span>
              <span className="batch-card-value">
                {batch.sub_vo_id && batch.t_sub_vo
                  ? `${batch.t_sub_vo.company_name} (ID: ${batch.sub_vo_id})`
                  : '-'}
              </span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Service:</span>
              <span className="batch-card-value">
                {batch.t_services ? batch.t_services.service_name : 'N/A'} (ID: {batch.service_id})
              </span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Tarif:</span>
              <span className="batch-card-value">
                {batch.t_tariffs ? batch.t_tariffs.tariff_name : 'N/A'} (ID: {batch.tariff_id})
              </span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Gültig ab:</span>
              <span className="batch-card-value">{formatDate(batch.validity_from_date)}</span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Gültig bis:</span>
              <span className="batch-card-value">{formatDate(batch.validity_until_date)}</span>
            </div>
            <div className="batch-card-item">
              <span className="batch-card-label">Verarbeitet (Status):</span>
              <span className="batch-card-value">
                {batch.is_locked
                  ? <span style={{ color: '#dc3545', fontWeight: 600 }}>Gesperrt</span>
                  : (batch.status ? batch.status.status_name : '-')}
              </span>
            </div>
            <Button 
              className="apple-blue-btn" 
              size="sm"
              style={{ marginTop: '10px', width: '100%' }}
              disabled={batch.is_processed || batch.is_locked} 
              onClick={() => handleCreateInvoiceClick(batch)}
            >
              Bestellen & Rechnung erstellen
            </Button>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <Button
                variant="outline-secondary"
                size="sm"
                style={{ flex: 1 }}
                disabled={batch.is_processed || batch.is_locked}
                onClick={() => handleEditClick(batch)}
              >
                <FaPencilAlt /> Bearbeiten
              </Button>
              <Button
                variant={batch.is_locked ? 'danger' : 'outline-danger'}
                size="sm"
                style={{ flex: 1 }}
                onClick={() => handleToggleLock(batch)}
              >
                {batch.is_locked ? <><FaLockOpen /> Entsperren</> : <><FaLock /> Sperren</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      <Pagination style={{ marginTop: '20px' }}>
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item 
            key={index} 
            active={index + 1 === currentPage} 
            onClick={() => setCurrentPage(index + 1)}
            style={{ borderColor: '#007AFF' }}
          >
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination>
      </div>
      
      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); setIsEditMode(false); setEditBatchId(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Charge bearbeiten' : 'Charge erstellen'}</Modal.Title>
        </Modal.Header>
        {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            <Form.Group controlId="batch_name">
              <Form.Label>Chargenname</Form.Label>
              <Form.Control
                type="text"
                name="batch_name"
                value={formData.batch_name}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!errors.batch_name}
                required
              />
              <Form.Control.Feedback type="invalid">{errors.batch_name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="batch_quantity">
              <Form.Label>Chargenmenge</Form.Label>
              <Form.Control
                type="number"
                name="batch_quantity"
                value={formData.batch_quantity}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!errors.batch_quantity}
                required
              />
              <Form.Control.Feedback type="invalid">{errors.batch_quantity}</Form.Control.Feedback>
            </Form.Group>
            
            {/* Folgeprompt 16 (Punkt 6): "Verarbeitet" bezieht die moeglichen
                Statusangaben aus den Stammdaten (/statuses) und speichert die
                gewaehlte status_id. Das System-Flag is_processed bleibt unberuehrt. */}
            <Form.Group controlId="status_id">
              <Form.Label>Verarbeitet</Form.Label>
              <Form.Control
                as="select"
                name="status_id"
                value={formData.status_id || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!errors.status_id}
              >
                <option value="">Status auswählen</option>
                {statuses.map((s) => (
                  <option key={s.status_id} value={s.status_id}>
                    {s.status_name}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                Statuswerte stammen aus den Stammdaten (Statusobjekte unter „Statusobjekte").
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {errors.status_id}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formProduct">
              <Form.Label>Produkt auswählen</Form.Label>
              <Form.Control
                as="select"
                value={selectedProductId}
                onChange={handleProductChange}
              >
                <option value="">Produkt auswählen</option>
                {products.map(prod => (
                  <option key={prod.showcase_id} value={prod.showcase_id}>
                    {prod.title}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                Auswahl aus systemseitig definierten Hauptprodukten.
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="formSubVo">
              <Form.Label>Service auswählen</Form.Label>
              <Form.Control
                as="select"
                name="service_id"
                value={formData.service_id}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!errors.service_id}
                disabled={!selectedProductId}
              >
                <option value="">
                  {selectedProductId
                    ? (filteredServices.length ? 'Service auswählen' : 'Keine Services für dieses Produkt')
                    : 'Bitte zuerst Produkt auswählen'}
                </option>
                {filteredServices.map(mvos => (
                  <option key={mvos.service_id} value={mvos.service_id}>
                    {mvos.service_name}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.service_id}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="participant_type">
              <Form.Label>Gültig für</Form.Label>
              <Form.Control
                as="select"
                name="participant_type"
                value={formData.participant_type || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!errors.participant_type}
                required={!isEditMode || !!formData.participant_type}
                disabled={participantTypeLocked || (!participantTypeLocked && !formData.service_id)}
              >
                <option value="">
                  {!formData.service_id
                    ? 'Bitte zuerst Service auswählen'
                    : (isEditMode ? 'Legacy: Mensch' : 'Bitte Zugangsart auswählen')}
                </option>
                {(participantTypeLocked ? ['HUMAN', 'AI', 'BOTH'] : allowedParticipantTypes).map((v) => (
                  <option key={v} value={v}>{PARTICIPANT_TYPE_LABELS[v]}</option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                {serviceSuitability === 'HUMAN' || serviceSuitability === 'AI'
                  ? `Der gewählte Service ist laut Produktdefinition nur für ${serviceSuitability === 'HUMAN' ? 'Menschen' : 'KI'} geeignet – andere Zugangsarten stehen nicht zur Auswahl. `
                  : ''}
                Die Chargenart legt fest, wer den Code verwendet. Das Tarifmodul ai_participant erlaubt den KI-Start; ai_interaction erlaubt Menschen nach eigener Zustimmung die KI-Interaktion.
                {participantTypeLocked
                  ? ' Nach Ausgabe, Verkauf, Zuordnung oder Einlösung ist diese Auswahl unveränderlich.'
                  : ''}
              </Form.Text>
              <Form.Control.Feedback type="invalid">{errors.participant_type}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="sales_channel">
              <Form.Label>Vertriebsart</Form.Label>
              <div className="myn-sales-channel">
                <Form.Check
                  type="checkbox"
                  id="batchSalesChannelB2b"
                  name="is_b2b"
                  label="B2B – Vertrieb über Wiederverkäufer und Vertriebspartner"
                  checked={!!formData.is_b2b}
                  onChange={handleChange}
                  className="myn-sales-channel__option"
                />
                <Form.Check
                  type="checkbox"
                  id="batchSalesChannelB2c"
                  name="is_b2c"
                  label="B2C – Endkunden buchen selbst auf myn.club"
                  checked={!!formData.is_b2c}
                  onChange={handleChange}
                  disabled={!!formData.service_id && !serviceHasDigitalTransport}
                  className="myn-sales-channel__option"
                />
              </div>
              <Form.Text className="text-muted">
                Mehrfachauswahl: Beide Vertriebsarten sind gleichzeitig möglich, mindestens eine ist erforderlich.
                {formData.service_id && !serviceHasDigitalTransport
                  ? ' B2C ist für diesen Dienst nicht wählbar, weil er ausschließlich physikalisch ausgeliefert wird. Bitte unter Stammdaten → Dienste eine digitale Transportart ergänzen.'
                  : ' B2C-Chargen erscheinen im öffentlichen Katalog der Selbstbucher.'}
              </Form.Text>
              <Form.Check
                type="checkbox"
                id="batchHomepageDemo"
                name="is_homepage_demo"
                label="Startseiten-Demo – Besucher können mit dieser Charge einen QR-Code testen, bevor sie sich registrieren"
                checked={!!formData.is_homepage_demo}
                onChange={handleChange}
                className="mt-2"
              />
              {!isSalesChannelValid() && (
                <div className="text-danger small mt-1">
                  Bitte mindestens eine Vertriebsart wählen.
                </div>
              )}
            </Form.Group>

            <Form.Group controlId="formTariff">
              <Form.Label>Tarif auswählen</Form.Label>
              <Form.Control
                type="text"
                value={tariffMenuOpen ? tariffQuery : (selectedTariff ? selectedTariff.tariff_name : "")}
                placeholder={formData.service_id ? "Tarifname eingeben" : "Bitte zuerst Service auswählen"}
                disabled={!formData.service_id}
                autoComplete="off"
                isInvalid={!!errors.tariff_id}
                onFocus={() => {
                  setTariffMenuOpen(true);
                  setTariffQuery("");
                }}
                onChange={(event) => {
                  setTariffQuery(event.target.value);
                  setTariffMenuOpen(true);
                }}
                onBlur={() => {
                  window.setTimeout(() => setTariffMenuOpen(false), 180);
                  handleBlur({ target: { name: "tariff_id", value: formData.tariff_id } });
                }}
              />
              {tariffMenuOpen && formData.service_id && (
                <div className="myn-tariff-menu" role="listbox" aria-label="Tarife">
                  {filteredTariffs
                    .filter((tariff) => {
                      const query = tariffQuery.trim().toLowerCase();
                      if (!query) return true;
                      return String(tariff.tariff_name || "").toLowerCase().includes(query);
                    })
                    .map((tariff) => (
                      <button
                        type="button"
                        key={tariff.tariff_id}
                        className="myn-tariff-menu__item"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          handleTariffChange({ target: { name: "tariff_id", value: tariff.tariff_id } });
                          setTariffQuery("");
                          setTariffMenuOpen(false);
                        }}
                      >
                        {tariff.tariff_name}
                        {tariff.qutel_sync ? ` (qutel${tariff.qutel_sequence ? `: ${tariff.qutel_sequence}` : ""})` : ""}
                      </button>
                    ))}
                  <button
                    type="button"
                    className="myn-tariff-menu__item myn-tariff-menu__create"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => navigate("/tariffs?neu=1")}
                  >
                    Neuen Tarif erstellen
                  </button>
                </div>
              )}
              {isQutelService && (
                <Form.Text className="text-muted d-block">
                  Es werden Tarife angezeigt, die diesem Produkt/Service zugeordnet sind oder qutel-Sync aktiviert haben.
                </Form.Text>
              )}
              {qutelRemaining != null && selectedTariff && (
                <Form.Text className="text-info d-block">
                  Restkontingent: {qutelRemaining} von {selectedTariff.qutel_max_codes} Codes
                  (bereits verplant ca. {usedCodesForTariff}).
                </Form.Text>
              )}
              {selectedTariff && !selectedTariff.code_charset && (
                <Form.Text className="text-danger d-block">
                  Am Tarif ist kein Codeformat (Zeichensatz/Länge) hinterlegt – Chargen-Anlage wird abgelehnt.
                </Form.Text>
              )}
              <Form.Control.Feedback type="invalid">
                {errors.tariff_id}
              </Form.Control.Feedback>
            </Form.Group>
          
            <Form.Group controlId="validity_from_date">
              <Form.Label>Gültig ab</Form.Label>
              <Form.Control
                type="date"
                name="validity_from_date"
                value={formData.validity_from_date}
                onChange={handleFromDateChange}
                onBlur={handleBlur}
                isInvalid={!!errors.validity_from_date}
                required
              />
              <Form.Text className="text-muted">
                Mit dem heutigen Datum vorbelegt – bei Bedarf änderbar.
              </Form.Text>
              <Form.Control.Feedback type="invalid">{errors.validity_from_date}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="validity_until_date">
              <Form.Label>Gültig bis</Form.Label>
              <Form.Control
                type="date"
                name="validity_until_date"
                value={formData.validity_until_date}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!errors.validity_until_date}
                required
              />
              <Form.Text className="text-muted">
                Wird aus der Laufzeit des gewählten Tarifs berechnet. Ohne hinterlegte
                Laufzeit bitte manuell ausfüllen.
              </Form.Text>
              <Form.Control.Feedback type="invalid">{errors.validity_until_date}</Form.Control.Feedback>
            </Form.Group>

            <Button className="apple-blue-btn" type="submit" style={{ marginTop: '20px' }} disabled={loading || !isFormEnabled}>
              {loading ? 'Speichern...' : (isEditMode ? 'Änderungen speichern' : 'Charge speichern')}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showCreateInvoiceModal} onHide={() => setshowCreateInvoiceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Bestellen und Rechnung erstellen</Modal.Title>
        </Modal.Header>
        {apiErrors && <Alert variant="danger">{apiErrors}</Alert>}
        <Modal.Body>
          <Form onSubmit={handleCreateInvoiceSubmit}>
              {/* NEUES FORMULARFELD FÜR BELEGART */}
              <Form.Group controlId="formDocType" className="mb-3"> 
                <Form.Label>Belegart auswählen</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedDocTypeId}
                  onChange={(e) => setSelectedDocTypeId(e.target.value)}
                  required
                >
                  <option value="">Bitte Belegart auswählen...</option>
                  {documentTypes.map(doc => (
                    <option key={doc.doc_type_id} value={doc.doc_type_id}>
                      {doc.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group controlId="formSubVo">
                <Form.Label>Wiederverkäufer auswählen</Form.Label>
                <Form.Control
                  as="select"
                  name="sub_vo_id"
                  value={formData.sub_vo_id || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.sub_vo_id}
                >
                  <option value="">Wiederverkäufer auswählen</option>
                  {subvos.map(mvos => (
                    <option key={mvos.sub_vo_id} value={mvos.sub_vo_id}>
                      {mvos.company_name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.sub_vo_id}
                </Form.Control.Feedback>
              </Form.Group>

              <Button className="apple-blue-btn" type="submit" disabled={loading || !formData.sub_vo_id || !selectedDocTypeId} style={{ marginTop: '20px' }}>
                  {loading ? 'Erstelle...' : 'Rechnung erstellen'}
              </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BatchManager;
